/**
 * Chat WebSocket context - keeps WebSocket connected app-wide when user is logged in.
 * This ensures read receipts and new messages are received even when not on the chat page.
 */
import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { getWebSocketUrl } from '../services/api';

const ChatWebSocketContext = createContext(null);

export const useChatWebSocketContext = () => useContext(ChatWebSocketContext);

export const ChatWebSocketProvider = ({ children, memberId }) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const messageHandlersRef = useRef(new Set());
  const readHandlersRef = useRef(new Set());
  const notificationHandlersRef = useRef(new Set());

  const connect = useCallback(() => {
    if (!memberId) return;

    const baseUrl = getWebSocketUrl();
    const url = `${baseUrl}/ws/chat?member_id=${encodeURIComponent(memberId)}`;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'new_message') {
          messageHandlersRef.current.forEach((handler) => {
            try {
              handler(payload);
            } catch (_) {}
          });
        } else if (payload.type === 'messages_read') {
          readHandlersRef.current.forEach((handler) => {
            try {
              handler(payload);
            } catch (_) {}
          });
        } else if (payload.type === 'notification' && payload.notification) {
          notificationHandlersRef.current.forEach((handler) => {
            try {
              handler(payload.notification);
            } catch (_) {}
          });
        }
      } catch (e) {
        // Ignore non-JSON or ping/pong
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 30000);
      reconnectAttemptRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // Don't call ws.close() here - it can close the socket before connection is established
      // and cause "WebSocket is closed before the connection is established". Let onclose handle cleanup.
    };

    wsRef.current = ws;
  }, [memberId]);

  useEffect(() => {
    const t = setTimeout(connect, 100);
    return () => {
      clearTimeout(t);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {}
        wsRef.current = null;
      }
    };
  }, [connect]);

  const registerHandlers = useCallback((onNewMessage, onMessagesRead) => {
    if (typeof onNewMessage === 'function') {
      messageHandlersRef.current.add(onNewMessage);
    }
    if (typeof onMessagesRead === 'function') {
      readHandlersRef.current.add(onMessagesRead);
    }
    return () => {
      if (typeof onNewMessage === 'function') {
        messageHandlersRef.current.delete(onNewMessage);
      }
      if (typeof onMessagesRead === 'function') {
        readHandlersRef.current.delete(onMessagesRead);
      }
    };
  }, []);

  const registerNotificationHandler = useCallback((callback) => {
    if (typeof callback === 'function') {
      notificationHandlersRef.current.add(callback);
    }
    return () => {
      if (typeof callback === 'function') {
        notificationHandlersRef.current.delete(callback);
      }
    };
  }, []);

  const value = { registerHandlers, registerNotificationHandler, isConnected: !!wsRef.current };

  return (
    <ChatWebSocketContext.Provider value={value}>
      {children}
    </ChatWebSocketContext.Provider>
  );
};
